import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import "./styles.css";
import { useNavigate } from "react-router-dom";
import { PLAN_DEFINITIONS } from "../../../config/plans";
import { createLeague, createRink, addNeighboringCity } from "../../../services/api/leagues";
import { fetchMySubscription } from "../../../services/api/subscriptions";
import { fetchMyProfile } from "../../../services/auth/profile-api";
import CityStateAutocomplete from "../../../components/CityStateAutocomplete";
import StateSelect from "../../../components/StateSelect";
import AddressAutocomplete from "../../../components/AddressAutocomplete";
import PhoneTextField from "../../../components/PhoneTextField";
import AuthPromptDialog from "../../../components/AuthPromptDialog";
import { hasLocallyValidSession } from "../../../services/auth/auth-session";

export default function NewLeagueView() {
  const navigate = useNavigate();
  const authPromptOpen = !hasLocallyValidSession();
  const [payoutMethod, setPayoutMethod] = React.useState("stripe");
  const [userSubscription, setUserSubscription] = React.useState(null);
  const isLaunchTier = !userSubscription || userSubscription?.planId === "launch";
  const activePlanDef = PLAN_DEFINITIONS.find((p) => p.id === (userSubscription?.planId ?? "launch")) ?? PLAN_DEFINITIONS[0];
  const participantLimit = activePlanDef.participantLimit;
  const rinkLimit = isLaunchTier ? 1 : Infinity;
  const [leagueName, setLeagueName] = React.useState("");
  const [leagueType, setLeagueType] = React.useState("PICKUP");
  const [skillLevel, setSkillLevel] = React.useState("ALL_WELCOME");
  const [ownerFirstName, setOwnerFirstName] = React.useState("");
  const [ownerLastName, setOwnerLastName] = React.useState("");
  const [ownerEmail, setOwnerEmail] = React.useState("");
  const [venmoHandle, setVenmoHandle] = React.useState("");
  const [city, setCity] = React.useState("");
  const [state, setState] = React.useState("");
  const [playerCostDollars, setPlayerCostDollars] = React.useState("");
  const [goalieCostDollars, setGoalieCostDollars] = React.useState("0");
  const [maxSkaterSpots, setMaxSkaterSpots] = React.useState("");
  const [maxGoalieSpots, setMaxGoalieSpots] = React.useState("");
  const [gamePaymentType, setGamePaymentType] = React.useState("DROP_IN");
  const [iceTimeCostDollars, setIceTimeCostDollars] = React.useState("");
  const [plannedGameCount, setPlannedGameCount] = React.useState("");
  const [saveState, setSaveState] = React.useState({ loading: false, message: "", error: "" });
  const [pendingRinks, setPendingRinks] = React.useState([]);
  const [rinkName, setRinkName] = React.useState("");
  const [rinkAddress, setRinkAddress] = React.useState("");
  const [rinkPhone, setRinkPhone] = React.useState("");
  const [addRinkError, setAddRinkError] = React.useState("");
  const [showOnlyRinkWarning, setShowOnlyRinkWarning] = React.useState(false);
  const [pendingNeighboringCities, setPendingNeighboringCities] = React.useState([]);
  const [neighboringCity, setNeighboringCity] = React.useState("");
  const [neighboringCityState, setNeighboringCityState] = React.useState("");
  const [addCityError, setAddCityError] = React.useState("");
  const [visibility, setVisibility] = React.useState("PUBLIC");
  const [useAllowedList, setUseAllowedList] = React.useState(false);

  const normalizeVenmoHandle = (raw) => {
    const stripped = raw.trim().replace(/^@+/, "");
    return stripped ? `@${stripped}` : "";
  };

  React.useEffect(() => {
    if (!hasLocallyValidSession()) return;
    fetchMySubscription().then((sub) => {
      if (!sub) {
        navigate("/pricing");
        return;
      }
      setUserSubscription(sub);
    }).catch(() => navigate("/pricing"));
    fetchMyProfile().then((profile) => {
      if (profile) {
        setOwnerEmail(profile.email || "");
        setOwnerFirstName(profile.firstName || "");
        setOwnerLastName(profile.lastName || "");
      }
    }).catch(() => {});
  }, [navigate]);

  React.useEffect(() => {
    // Wait for the subscription to load: before it does, isLaunchTier is true for everyone,
    // which would wrongly flip paid-plan users off the Stripe default.
    if (userSubscription && isLaunchTier && payoutMethod === "stripe") {
      setPayoutMethod("venmo");
    }
  }, [userSubscription, isLaunchTier, payoutMethod]);

  const recalcPlayerCost = (iceCost, skaterSpots, gameCount, paymentType) => {
    const cost = parseFloat(iceCost);
    const spots = parseInt(skaterSpots, 10);
    if (isNaN(cost) || isNaN(spots) || spots <= 0) return;
    if (paymentType === "FULL_LEAGUE") {
      setPlayerCostDollars((cost / spots).toFixed(2));
    } else {
      const games = parseInt(gameCount, 10);
      if (!isNaN(games) && games > 0) {
        setPlayerCostDollars((cost / spots / games).toFixed(2));
      }
    }
  };

  React.useEffect(() => {
    recalcPlayerCost(iceTimeCostDollars, maxSkaterSpots, plannedGameCount, gamePaymentType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iceTimeCostDollars, maxSkaterSpots, plannedGameCount, gamePaymentType]);

  const handleSave = async () => {
    const rinksToCreate = [...pendingRinks];
    const currentRinkName = rinkName.trim();
    const currentRinkAddress = rinkAddress.trim();
    if (currentRinkName && currentRinkAddress) {
      rinksToCreate.push({ name: currentRinkName, address: currentRinkAddress, rinkPhone: rinkPhone.trim() || undefined });
    }
    if (rinksToCreate.length === 0) {
      setSaveState({ loading: false, message: "", error: "At least one rink is required." });
      return;
    }
    const skaters = parseInt(maxSkaterSpots, 10);
    const goalies = parseInt(maxGoalieSpots, 10);
    const totalCapacity = (isNaN(skaters) ? 0 : skaters) + (isNaN(goalies) ? 0 : goalies);
    if (totalCapacity > participantLimit) {
      setSaveState({ loading: false, message: "", error: `Total roster capacity (${totalCapacity}) exceeds your ${activePlanDef.name} plan limit of ${participantLimit}. Reduce capacity or upgrade your plan.` });
      return;
    }
    setSaveState({ loading: true, message: "", error: "" });
    try {
      const league = await createLeague({
        name: leagueName,
        leagueType,
        skillLevel,
        planId: userSubscription?.planId ?? "launch",
        payoutMethod,
        venmoHandle: payoutMethod === "venmo" ? (normalizeVenmoHandle(venmoHandle) || undefined) : undefined,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        playerCost: payoutMethod !== "none" && playerCostDollars !== "" ? Math.round(parseFloat(playerCostDollars) * 100) : 0,
        goalieCost: payoutMethod !== "none" && goalieCostDollars !== "" ? Math.round(parseFloat(goalieCostDollars) * 100) : 0,
        maxSkaterSpots: maxSkaterSpots !== "" ? parseInt(maxSkaterSpots, 10) : undefined,
        maxGoalieSpots: maxGoalieSpots !== "" ? parseInt(maxGoalieSpots, 10) : undefined,
        gamePaymentType: payoutMethod === "none" ? "DROP_IN" : gamePaymentType,
        iceTimeCost: payoutMethod !== "none" && iceTimeCostDollars !== "" ? Math.round(parseFloat(iceTimeCostDollars) * 100) : undefined,
        visibility,
        useAllowedList: visibility === "PRIVATE" ? useAllowedList : false,
        owner: {
          email: ownerEmail,
          firstName: ownerFirstName,
          lastName: ownerLastName,
        },
      });

      for (const rink of rinksToCreate) {
        await createRink(league.id, {
          name: rink.name,
          address: rink.address,
          rinkPhone: rink.rinkPhone || undefined,
        });
      }

      const citiesToCreate = [...pendingNeighboringCities];
      const currentNeighCity = neighboringCity.trim();
      const currentNeighState = neighboringCityState.trim();
      if (currentNeighCity && currentNeighState) {
        citiesToCreate.push({ city: currentNeighCity, state: currentNeighState });
      }
      for (const nc of citiesToCreate) {
        await addNeighboringCity(league.id, { city: nc.city, state: nc.state });
      }

      setSaveState({ loading: false, message: "", error: "" });
      navigate("/create-game", { state: { fromNewLeague: true, leagueId: league.id } });
    } catch (error) {
      setSaveState({
        loading: false,
        message: "",
        error: error instanceof Error ? error.message : "Failed to save league",
      });
    }
  };

  const handleAddRink = () => {
    const name = rinkName.trim();
    const address = rinkAddress.trim();
    if (!name || !address) {
      setAddRinkError("Rink name and address are required.");
      return;
    }
    setAddRinkError("");
    setPendingRinks((prev) => [
      ...prev,
      { name, address, rinkPhone: rinkPhone.trim() || undefined },
    ]);
    setRinkName("");
    setRinkAddress("");
    setRinkPhone("");
  };

  const handleAddNeighboringCity = (cityArg, stateArg) => {
    const cityVal = (cityArg ?? neighboringCity).trim();
    const st = (stateArg ?? neighboringCityState).trim().toUpperCase();
    if (!cityVal || !st) {
      setAddCityError("City and state are required.");
      return;
    }
    if (pendingNeighboringCities.some(c => c.city.toLowerCase() === cityVal.toLowerCase() && c.state === st)) {
      setAddCityError("That city/state is already in the list.");
      return;
    }
    setAddCityError("");
    setPendingNeighboringCities(prev => [...prev, { city: cityVal, state: st }]);
    setNeighboringCity("");
    setNeighboringCityState("");
  };

  return (
    <Container maxWidth="md" className="new-league-page">
      <AuthPromptDialog
        open={authPromptOpen}
        onClose={() => navigate("/")}
        onConfirm={() => navigate("/")}
        title="Sign in required"
        body="You need to sign in and have an active subscription to create a league."
      />
      <Stack spacing={2}>
        <Typography variant="h3" component="h3">
          Create Your Hockey League
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Set up your rink operations, billing, waivers, and role access for your hockey pickup events or leagues.
        </Typography>
      </Stack>

      <Card variant="outlined" className="section-card">
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h5">League Profile</Typography>
            <TextField
              label="League Name"
              placeholder="Downtown Adult Pickup Hockey"
              value={leagueName}
              onChange={(event) => setLeagueName(event.target.value)}
            />
            <FormControl>
              <FormLabel>League Type</FormLabel>
              <RadioGroup
                row
                value={leagueType}
                onChange={(e) => setLeagueType(e.target.value)}
              >
                <FormControlLabel value="PICKUP" control={<Radio />} label="Pickup" />
                <FormControlLabel value="COMPETITIVE" control={<Radio />} label="Competitive (league/tournament)" />
              </RadioGroup>
            </FormControl>
            <TextField
              select
              fullWidth
              label="Skill level"
              value={skillLevel}
              onChange={(e) => setSkillLevel(e.target.value)}
              helperText="Default skill level for all games in this league."
            >
              <MenuItem value="ALL_WELCOME">All welcome</MenuItem>
              <MenuItem value="C_BEGINNER">C / Beginner</MenuItem>
              <MenuItem value="B_C">B/C</MenuItem>
              <MenuItem value="B">B</MenuItem>
              <MenuItem value="A_B">A/B</MenuItem>
              <MenuItem value="A">A</MenuItem>
            </TextField>
            <FormLabel>Primary Location</FormLabel>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <CityStateAutocomplete
                cityValue={city}
                stateValue={state}
                onCityStateChange={(newCity, newState) => {
                  setCity(newCity);
                  setState(newState);
                }}
                label="City"
                placeholder="e.g. Brooklyn Park"
                sx={{ flex: 1 }}
              />
              <StateSelect value={state} onChange={setState} sx={{ width: 180 }} />
            </Stack>

            <Divider />

            <FormLabel>Neighboring Cities</FormLabel>
            <Typography variant="body2" color="text.secondary">
              Add nearby cities so players searching from those areas can find your games too.
            </Typography>
            {pendingNeighboringCities.length > 0 && (
              <Stack spacing={0.5}>
                {pendingNeighboringCities.map((nc, i) => (
                  <Stack key={i} direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 0.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                    <Typography variant="body2">{nc.city}, {nc.state}</Typography>
                    <IconButton size="small" onClick={() => setPendingNeighboringCities(prev => prev.filter((_, idx) => idx !== i))} aria-label="Remove city">
                      <DeleteOutlineIcon fontSize="small" color="error" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
              <CityStateAutocomplete
                cityValue={neighboringCity}
                stateValue={neighboringCityState}
                onCityStateChange={(c, s) => { setNeighboringCity(c); setNeighboringCityState(s); }}
                onSubmit={handleAddNeighboringCity}
                label="Neighboring city"
                placeholder="e.g. Maple Grove"
                sx={{ flex: 1 }}
              />
              <StateSelect value={neighboringCityState} onChange={setNeighboringCityState} sx={{ width: 180 }} />
            </Stack>
            {addCityError ? <Alert severity="error" onClose={() => setAddCityError("")}>{addCityError}</Alert> : null}
            <Button variant="outlined" onClick={() => handleAddNeighboringCity()} disabled={!neighboringCity.trim() || !neighboringCityState.trim()}>
              Add neighboring city
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" className="section-card">
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
              <Typography variant="h5">Rinks</Typography>
              {isLaunchTier && (
                <Chip size="small" label="1 rink max · Launch plan" variant="outlined" />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Add one or more rinks for this league. They will be created when you save below. Games will be tied to a specific rink.
            </Typography>
            {pendingRinks.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Rinks to create ({pendingRinks.length})
                </Typography>
                <Stack spacing={0.5}>
                  {pendingRinks.map((r, i) => {
                    const isOnly = pendingRinks.length <= 1;
                    return (
                      <Stack key={i} direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 0.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                        <Typography variant="body2">
                          {r.name} — {r.address}{r.rinkPhone ? ` · ${r.rinkPhone}` : ""}
                        </Typography>
                        <Tooltip
                          title="At least one rink is required. Add another rink to the list before removing this one."
                          open={isOnly && showOnlyRinkWarning}
                          onClose={() => setShowOnlyRinkWarning(false)}
                          disableHoverListener
                          disableFocusListener
                          disableTouchListener
                          arrow
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (isOnly) { setShowOnlyRinkWarning(true); setTimeout(() => setShowOnlyRinkWarning(false), 4000); return; }
                              setPendingRinks(prev => prev.filter((_, idx) => idx !== i));
                            }}
                            aria-label="Remove rink"
                          >
                            <DeleteOutlineIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            )}
            {pendingRinks.length >= rinkLimit ? (
              <Alert severity="info">
                Your <strong>{activePlanDef.name}</strong> plan supports {rinkLimit} rink per league.{" "}
                <strong>Upgrade your plan</strong> to add multiple rinks.
              </Alert>
            ) : (
              <>
                <Typography variant="subtitle2" color="text.secondary">Add a rink</Typography>
                <TextField
                  label="Rink name"
                  placeholder="e.g. Maple Arena - Rink B"
                  value={rinkName}
                  onChange={(e) => setRinkName(e.target.value)}
                  fullWidth
                />
                <AddressAutocomplete
                  value={rinkAddress}
                  onAddressChange={setRinkAddress}
                  label="Address"
                  placeholder="Start typing address or facility name for suggestions"
                  required
                />
                <PhoneTextField
                  label="Phone (optional)"
                  value={rinkPhone}
                  onChange={setRinkPhone}
                  fullWidth
                />
                {addRinkError ? (
                  <Alert severity="error" onClose={() => setAddRinkError("")}>
                    {addRinkError}
                  </Alert>
                ) : null}
                <Button
                  variant="outlined"
                  onClick={handleAddRink}
                  disabled={!rinkName.trim() || !rinkAddress.trim()}
                >
                  Add rink to list
                </Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" className="section-card">
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h5">Payment Method</Typography>
            <FormControl>
              <FormLabel>How should participant payments return to the organizer?</FormLabel>
              <RadioGroup
                value={payoutMethod}
                onChange={(event) => {
                  setPayoutMethod(event.target.value);
                  if (event.target.value === "none") {
                    setVisibility("PRIVATE");
                    setGamePaymentType("DROP_IN");
                  }
                }}
              >
                <FormControlLabel
                  value="stripe"
                  control={<Radio disabled={isLaunchTier} />}
                  label={
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <span>Stripe Connect (recommended)</span>
                      {isLaunchTier && (
                        <Typography variant="caption" color="text.secondary">
                          — Not available on the Launch plan. Upgrade to enable.
                        </Typography>
                      )}
                    </Stack>
                  }
                />
                <FormControlLabel value="venmo" control={<Radio />} label="Venmo" />
                <FormControlLabel value="none" control={<Radio />} label="No payment — organizer covers the cost" />
              </RadioGroup>
            </FormControl>
            {payoutMethod === "venmo" ? (
              <TextField
                label="Organizer Venmo Handle"
                placeholder="organizer's venmo handler"
                value={venmoHandle}
                onChange={(event) => setVenmoHandle(event.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">@</InputAdornment> }}
              />
            ) : null}
            {payoutMethod !== "none" && (
              <Typography variant="body2" color="text.secondary">
                Subscription billing is always charged to the organizer.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" className="section-card">
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h5">League capacity & cost</Typography>
            <FormLabel>Roster Size</FormLabel>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Skaters per game"
                type="number"
                inputProps={{ min: 0, max: participantLimit }}
                placeholder="20"
                value={maxSkaterSpots}
                onChange={(e) => setMaxSkaterSpots(e.target.value)}
                error={
                  maxSkaterSpots !== "" && maxGoalieSpots !== "" &&
                  parseInt(maxSkaterSpots, 10) + parseInt(maxGoalieSpots, 10) > participantLimit
                }
                helperText="Max skaters per game"
              />
              <TextField
                label="Goalies per game"
                type="number"
                inputProps={{ min: 0, max: participantLimit }}
                placeholder="2"
                value={maxGoalieSpots}
                onChange={(e) => setMaxGoalieSpots(e.target.value)}
                error={
                  maxSkaterSpots !== "" && maxGoalieSpots !== "" &&
                  parseInt(maxSkaterSpots, 10) + parseInt(maxGoalieSpots, 10) > participantLimit
                }
                helperText="Max goalies per game"
              />
            </Stack>
            {(() => {
              const skaters = parseInt(maxSkaterSpots, 10);
              const goalies = parseInt(maxGoalieSpots, 10);
              const total = (isNaN(skaters) ? 0 : skaters) + (isNaN(goalies) ? 0 : goalies);
              const overLimit = total > participantLimit;
              const showCount = maxSkaterSpots !== "" || maxGoalieSpots !== "";
              if (!showCount) return null;
              return (
                <Typography
                  variant="caption"
                  color={overLimit ? "error" : "text.secondary"}
                >
                  {overLimit
                    ? `Total exceeds your ${activePlanDef.name} plan limit of ${participantLimit} participants. Reduce capacity or upgrade your plan.`
                    : `Total capacity: ${total} / ${participantLimit} participants (${activePlanDef.name} plan)`}
                </Typography>
              );
            })()}
            {payoutMethod === "none" ? (
              <Alert severity="info" sx={{ py: 0.5, "& .MuiAlert-message": { fontSize: "0.875rem" } }}>
                Players won&apos;t be charged, and each player can sign up for all games or just the individual games they plan to attend (drop-in). Payment type and pricing are hidden because the organizer is covering the cost.
              </Alert>
            ) : (
              <>
            <FormControl>
              <FormLabel>Payment Type</FormLabel>
              <RadioGroup row value={gamePaymentType} onChange={(e) => setGamePaymentType(e.target.value)}>
                <FormControlLabel value="DROP_IN" control={<Radio />} label="Drop-in (players pay per game)" />
                <FormControlLabel value="FULL_LEAGUE" control={<Radio />} label="Full league (players pay once for all games)" />
              </RadioGroup>
            </FormControl>
            <TextField
              label="Total ice rental cost for this league ($)"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              placeholder="e.g. 300"
              value={iceTimeCostDollars}
              onChange={(e) => setIceTimeCostDollars(e.target.value)}
              helperText={(() => {
                const cost = parseFloat(iceTimeCostDollars);
                const spots = parseInt(maxSkaterSpots, 10);
                const hasValidInputs = iceTimeCostDollars !== "" && !isNaN(cost) && maxSkaterSpots !== "" && !isNaN(spots) && spots > 0;
                if (gamePaymentType === "FULL_LEAGUE") {
                  if (hasValidInputs) {
                    const breakEven = (cost / spots).toFixed(2);
                    const current = playerCostDollars !== "" ? parseFloat(playerCostDollars).toFixed(2) : null;
                    return `At ${spots} skaters, break-even is $${breakEven}/player.${current != null ? ` Your current player price: $${current}.` : ""}`;
                  }
                  return "Enter your total ice rental cost per session to see break-even pricing suggestions.";
                }
                // DROP_IN
                if (hasValidInputs) {
                  const totalBreakEven = (cost / spots).toFixed(2);
                  const games = parseInt(plannedGameCount, 10);
                  if (!isNaN(games) && games > 0) {
                    return `At ${spots} skaters across ${games} games, break-even is $${(cost / spots / games).toFixed(2)}/player per game. Player cost updated below.`;
                  }
                  return `At ${spots} skaters, total break-even is $${totalBreakEven}/player. Divide that by the number of games you plan to host.`;
                }
                return "Enter your total ice rental cost per session to see break-even pricing suggestions.";
              })()}
              fullWidth
            />
            {gamePaymentType === "DROP_IN" && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: -0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  ÷ estimated games:
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  variant="standard"
                  inputProps={{ min: 1, step: 1 }}
                  placeholder="e.g. 10"
                  value={plannedGameCount}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPlannedGameCount(val);
                    recalcPlayerCost(iceTimeCostDollars, maxSkaterSpots, val, gamePaymentType);
                  }}
                  sx={{ width: 90 }}
                />
              </Stack>
            )}
            <FormLabel>Cost per game - Goalie cost defaults to $0</FormLabel>                                   
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: "8px !important" }}>  
              <TextField
                label="Player cost ($)"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                placeholder="7"
                value={playerCostDollars}
                onChange={(e) => setPlayerCostDollars(e.target.value)}
              />
              <TextField
                label="Goalie cost ($)"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                placeholder="0"
                value={goalieCostDollars}
                onChange={(e) => setGoalieCostDollars(e.target.value)}
                helperText="Default $0"
              />
            </Stack>
            <Alert severity="info" sx={{ py: 0.5, "& .MuiAlert-message": { fontSize: "0.875rem" } }}>
              Player and goalie costs cannot be changed after participants have joined the league.
            </Alert>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" className="section-card">
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h5">League Visibility</Typography>
            <FormControl>
              <FormLabel>Who can find and join this league?</FormLabel>
              <RadioGroup value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                <FormControlLabel value="PUBLIC" control={<Radio disabled={payoutMethod === "none"} />} label="Public — visible in search results, open sign-ups" />
                <FormControlLabel value="PRIVATE" control={<Radio />} label="Private — hidden from search" />
              </RadioGroup>
            </FormControl>
            {payoutMethod === "none" && (
              <Typography variant="body2" color="text.secondary">
                Organizer-covered leagues are always private. This protects the league from uninvited players showing up without an explicit invite.
              </Typography>
            )}
            {visibility === "PRIVATE" && (
              <Stack spacing={2} sx={{ pl: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useAllowedList}
                      onChange={(e) => setUseAllowedList(e.target.checked)}
                    />
                  }
                  label="Restrict sign-ups to an allowed list"
                />
                {useAllowedList ? (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Only emails on the allowed list can view the league and register for games. Add them from your league management page after creating.
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Anyone with the direct link (your QR code URL) can view and sign up. The league won&apos;t appear in public search.
                  </Alert>
                )}
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" className="section-card">
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h5">Owner Contact</Typography>
            <TextField
              label="Owner First Name"
              value={ownerFirstName}
              onChange={(event) => setOwnerFirstName(event.target.value)}
            />
            <TextField
              label="Owner Last Name"
              value={ownerLastName}
              onChange={(event) => setOwnerLastName(event.target.value)}
            />
            <TextField
              label="Owner Email"
              type="email"
              value={ownerEmail}
              onChange={(event) => setOwnerEmail(event.target.value)}
            />
          </Stack>
        </CardContent>
      </Card>

      <Box className="cta-row">
        <Button variant="contained" size="large" onClick={handleSave} disabled={saveState.loading}>
          {saveState.loading ? "Saving..." : "Save Hockey League Setup"}
        </Button>
        <Button variant="text" onClick={() => navigate("/")}>
          Back to Home
        </Button>
      </Box>

      {saveState.message ? <Alert severity="success">{saveState.message}</Alert> : null}
      {saveState.error ? <Alert severity="error">{saveState.error}</Alert> : null}
    </Container>
  );
}
